import React from 'react';
import { Copy } from 'iconoir-react';
import { Clipboard, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHAT_MESSAGE_ACTION_ICON_PROPS } from 'constant/chat-layout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useToast } from '../../../hooks/use-toast';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import {
  copyPlainTextWithoutMarkdown,
  copyRawText,
} from '../utils/copy-chat-message';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';

interface CopyMessageDropdownProps {
  content: string;
  generationId: string;
  generatedImages?: Array<{ fileId: string;[key: string]: any }>;
  disabled?: boolean;
  className?: string;
}

export const CopyMessageDropdown: React.FC<CopyMessageDropdownProps> = ({
  content,
  generationId,
  generatedImages,
  disabled,
  className,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleCopyImage = async (fileId: string) => {
    try {
      const itemIdFilePathMap = useChatStore.getState().itemIdFilePathMap;
      const imageSrc = itemIdFilePathMap.get(fileId);

      if (!imageSrc) {
        toast({
          variant: 'destructive',
          title: t('FAILED_TO_COPY_IMAGE') || 'Failed to copy image',
          description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD') || 'Failed to copy image to clipboard',
        });
        return;
      }

      const response = await fetch(imageSrc);
      if (!response.ok) {
        trackEvent(
          EVENTS.ERROR(new Error('Failed to copy image'), {
            endpoint: imageSrc,
            method: 'GET',
            status: response.status,
          })
        );
      }
      const blob = await response.blob();
      const imageBlob = new Blob([blob], { type: 'image/png' });

      if (typeof navigator !== 'undefined' && 'ClipboardItem' in window && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new (window as any).ClipboardItem({
            'image/png': imageBlob,
          }),
        ]);

        toast({
          variant: 'success',
          title: t('COPIED') || 'Copied',
          description: t('IMAGE_COPY_SUCCESS') || 'Image copied to clipboard',
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('FAILED_TO_COPY_IMAGE') || 'Failed to copy image',
          description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD') || 'Failed to copy image to clipboard',
        });
      }
    } catch (error) {
      console.error('Failed to copy image:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_IMAGE') || 'Failed to copy image',
        description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD') || 'Failed to copy image to clipboard',
      });
    }
  };

  const handleCopyWithStyling = async () => {
    const hasImages = (generatedImages ?? []).length > 0;
    if (hasImages && generatedImages?.[0]?.fileId) {
      await handleCopyImage(generatedImages[0].fileId);
      return;
    }

    // Previous functionality: copy markdown content as-is (.md raw response)
    const success = await copyRawText(content);
    if (success) {
      toast({
        variant: 'success',
        title: t('COPIED') || 'Copied',
        description: t('TEXT_COPY_SUCCESS') || 'Text copied to clipboard',
      });
    } else {
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT') || 'Failed to copy text',
        description: t('FAILED_COPY_TO_CLIPBOARD') || 'Failed to copy to clipboard',
      });
    }
  };

  const handleCopyRaw = async () => {
    const messageElement = document.getElementById(`chat-message-content-${generationId}`);
    const success = await copyPlainTextWithoutMarkdown(messageElement, content);
    if (success) {
      toast({
        variant: 'success',
        title: t('COPIED') || 'Copied',
        description: t('TEXT_COPY_SUCCESS') || 'Text copied to clipboard',
      });
    } else {
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT') || 'Failed to copy text',
        description: t('FAILED_COPY_TO_CLIPBOARD') || 'Failed to copy to clipboard',
      });
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={t('COPY') || 'Copy'}
          className={
            className ||
            'flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none'
          }
        >
          <Copy {...CHAT_MESSAGE_ACTION_ICON_PROPS} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="min-w-[190px] w-auto p-1">
        <DropdownMenuItem
          onClick={handleCopyWithStyling}
          className="cursor-pointer flex items-center gap-2"
        >
          <Clipboard className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{t('COPY_WITH_STYLING') || 'Copy with styling'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleCopyRaw}
          className="cursor-pointer flex items-center gap-2"
        >
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{t('COPY_RAW_RESPONSE') || 'Copy raw response'}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
